import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PropertyCard } from "@/components/house/PropertyCard";
import { MembersList } from "@/components/house/MembersList";
import { JoinHouseDialog } from "@/components/house/JoinHouseDialog";
import { useAuth } from "@/components/AuthProvider";

const HouseDetails = () => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Check if user is a member of any house
  const { data: isMember = false, isLoading: isLoadingMembership } = useQuery({
    queryKey: ['user-house-membership', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('house_members')
        .select('id')
        .eq('user_id', user!.id)
        .limit(1);

      if (error) {
        console.error('Error checking membership:', error);
        return false;
      }

      return data.length > 0;
    }
  });

  // Fetch house data
  const { data: house, isLoading: isLoadingHouse } = useQuery({
    queryKey: ['house'],
    enabled: isMember,
    queryFn: async () => {
      const { data: houses, error: housesError } = await supabase
        .from('houses')
        .select('*');

      if (housesError) {
        console.error('Error fetching houses:', housesError);
        toast({
          title: "Error",
          description: "No se pudieron cargar los detalles de la casa",
          variant: "destructive",
        });
        throw housesError;
      }

      if (!houses || houses.length === 0) {
        return null;
      }

      if (houses.length > 1) {
        toast({
          title: "Nota",
          description: "Se encontraron múltiples casas. Mostrando la primera.",
        });
      }

      return houses[0];
    }
  });

  const { data: members = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ['houseMembers', house?.id],
    enabled: !!house?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('house_members')
        .select(`
          *,
          profile:profiles(*)
        `)
        .eq('house_id', house!.id);

      if (error) {
        console.error('Error fetching house members:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los miembros de la casa",
          variant: "destructive",
        });
        throw error;
      }

      return data || [];
    }
  });

  const handleAddMember = (email: string) => {
    if (!email) {
      toast({
        title: "Error",
        description: "Por favor ingrese un correo electrónico",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "No implementado",
      description: "La funcionalidad de agregar miembros será implementada pronto",
    });
  };

  const handleRemoveMember = (memberId: string) => {
    toast({
      title: "No implementado",
      description: "La funcionalidad de eliminar miembros será implementada pronto",
    });
  };

  if (isLoadingHouse || isLoadingMembership) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isMember) {
    return (
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
          <h1 className="text-2xl font-bold text-gray-800">No eres miembro de ninguna casa</h1>
          <p className="text-muted-foreground text-center">Para ver los detalles de una casa, primero debes unirte a una.</p>
          <JoinHouseDialog />
        </div>
      </div>
    );
  }

  if (!house) {
    return (
      <div className="container mx-auto px-4 py-6 md:py-8">
        <h1 className="text-2xl font-bold text-gray-800">No se encontró la casa</h1>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 md:py-8">
      <div className="flex flex-col mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Detalles de la casa</h1>
        <p className="text-muted-foreground mt-1">Administra la información y miembros de tu casa</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-full">
        <PropertyCard name={house.name} address={house.address} />
        <MembersList
          members={members}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
        />
      </div>
    </div>
  );
};

export default HouseDetails;